import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Wallet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Loader, fmt } from "@/components/shared";
import { PlansBackdrop, PlanCard, GlowCta } from "@/components/PlanVisuals";
import { InvestmentsList } from "./InvestmentsList";

function InvestDialog({ plan, balance, onClose, onDone }) {
  const [amount, setAmount] = useState(plan ? String(plan.amount) : "");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (plan) setAmount(String(plan.amount)); }, [plan]);
  if (!plan) return null;
  const amt = parseFloat(amount);
  const daily = isNaN(amt) ? 0 : amt * plan.daily_percentage / 100;

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/investments", { plan_id: plan.id, amount: amt });
      toast.success(`${fmt(amt)} USDT invested in ${plan.name}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0A0F1D] border-cyan-500/20 text-slate-200" data-testid="invest-dialog">
        <DialogHeader><DialogTitle className="text-white">Invest in {plan.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="glass rounded-xl p-3 flex items-center justify-between text-sm">
            <span className="text-slate-400 flex items-center gap-2"><Wallet className="w-4 h-4 text-cyan-400" /> Available balance</span>
            <span className="font-mono text-white" data-testid="invest-balance">{fmt(balance)} USDT</span>
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Amount (USDT) — {fmt(plan.min_amount)} to {fmt(plan.max_amount)}</label>
            <input data-testid="invest-amount" type="number" step="0.01" min={plan.min_amount} max={plan.max_amount} value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 font-mono focus:border-cyan-400 focus:outline-none" />
          </div>
          <div className="text-xs text-slate-400 space-y-1">
            <p>Daily ROI: <span className="font-mono text-emerald-300">{fmt(daily)} USDT</span> per working day ({plan.daily_percentage}% of principal, simple interest, variable & not guaranteed) · max total ROI {fmt(daily * plan.duration_days)} USDT</p>
            <p>Duration: {plan.duration_days} working days (Mon–Fri only; Saturday & Sunday do not count). Principal is locked and returned to your balance at maturity.</p>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="glass glass-hover px-4 py-2 rounded-lg text-sm text-slate-300">Cancel</button>
          <button onClick={submit} disabled={loading || isNaN(amt) || amt <= 0} data-testid="invest-confirm" className="btn-glow px-5 py-2 rounded-lg text-sm font-semibold text-slate-950 inline-flex items-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Confirm Investment
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PlansPortal() {
  const [plans, setPlans] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [selected, setSelected] = useState(null);
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
    api.get("/investments").then((r) => setInvestments(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const balance = user?.available_balance || 0;

  return (
    <div className="relative space-y-6" data-testid="portal-plans">
      <PlansBackdrop />
      <div className="relative flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">Investment Plans</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">Select a Plan</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-2xl">Approved deposits are auto-placed into the active plan. ROI is 1% of your original principal per working day (Mon–Fri), never compounded, for 200 working days. Percentages are variable and non-guaranteed.</p>
        </div>
        <div className="glass rounded-xl px-5 py-3 text-sm" data-testid="plans-balance">
          <span className="text-slate-400">Available:</span> <span className="font-mono text-white ml-1">{fmt(balance)} USDT</span>
        </div>
      </div>
      {plans === null ? <Loader /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 [&>*]:w-full [&>*]:max-w-md lg:[&:has(>:only-child)]:grid-cols-1">
          {plans.map((p, i) => {
            const mine = investments.filter((x) => x.plan_id === p.id && x.status === "active");
            const done = mine.length ? Math.max(...mine.map((x) => x.days_credited)) : 0;
            return (
              <PlanCard key={p.id} plan={p} index={i} activeDays={done}
                cta={balance >= p.min_amount
                  ? <GlowCta onClick={() => setSelected(p)} testId={`invest-btn-${p.amount}`}>Invest Now</GlowCta>
                  : <GlowCta onClick={() => nav("/deposit")} testId={`deposit-btn-${p.amount}`} className="!bg-none !bg-slate-800/80 !text-slate-100 border border-cyan-500/20">Deposit to Start</GlowCta>} />
            );
          })}
        </div>
      )}
      <InvestmentsList items={investments} />
      <InvestDialog plan={selected} balance={balance} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); refresh(); }} />
    </div>
  );
}
